import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  site_name: "Herbalife Shop",
  title_template: "%s | Herbalife Shop",
  default_title: "",
  default_description: "",
  default_image: "",
  default_keywords: "",
  locale: "fr_FR",
  twitter_handle: "",
  organization_url: "",
  robots_default: "index,follow",
};

export default function SeoGlobalEditor() {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/site/seo-global");
        setData({ ...EMPTY, ...r.data });
      } catch {
        toast.error("Impossible de charger les réglages SEO globaux");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/site/seo-global", data);
      toast.success("Réglages SEO globaux mis à jour");
      // Hint user that pages will refresh next navigation
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div data-testid="seo-global-loading" className="py-8 text-center text-sm text-[#4B634B]">Chargement…</div>;
  }

  return (
    <form onSubmit={submit} className="space-y-4 max-w-2xl" data-testid="seo-global-editor">
      <div>
        <Label>Nom du site</Label>
        <Input data-testid="seo-global-site-name" value={data.site_name} onChange={(e) => set("site_name", e.target.value)} maxLength={80} required />
      </div>
      <div>
        <Label>Gabarit de titre <span className="text-xs text-[#4B634B]">(utiliser %s pour le titre de la page)</span></Label>
        <Input data-testid="seo-global-title-template" value={data.title_template} onChange={(e) => set("title_template", e.target.value)} maxLength={120} required />
      </div>
      <div>
        <Label>Titre par défaut (page d'accueil)</Label>
        <Input data-testid="seo-global-default-title" value={data.default_title} onChange={(e) => set("default_title", e.target.value)} maxLength={120} />
      </div>
      <div>
        <Label>Description par défaut</Label>
        <Textarea data-testid="seo-global-default-description" value={data.default_description} onChange={(e) => set("default_description", e.target.value)} rows={3} maxLength={320} />
      </div>
      <div>
        <Label>Image Open Graph par défaut</Label>
        <Input data-testid="seo-global-default-image" value={data.default_image} onChange={(e) => set("default_image", e.target.value)} placeholder="https://… ou /api/files/…" />
        <p className="text-xs text-[#4B634B] mt-1">Image affichée lors d'un partage sur les réseaux sociaux. 1200×630 px.</p>
      </div>
      <div>
        <Label>Mots-clés par défaut</Label>
        <Input data-testid="seo-global-default-keywords" value={data.default_keywords} onChange={(e) => set("default_keywords", e.target.value)} maxLength={300} placeholder="herbalife, nutrition, …" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Locale</Label>
          <Input data-testid="seo-global-locale" value={data.locale} onChange={(e) => set("locale", e.target.value)} placeholder="fr_FR" maxLength={10} />
        </div>
        <div>
          <Label>Handle Twitter (avec @)</Label>
          <Input data-testid="seo-global-twitter" value={data.twitter_handle} onChange={(e) => set("twitter_handle", e.target.value)} placeholder="@herbalifeshop" maxLength={40} />
        </div>
      </div>
      <div>
        <Label>URL officielle de l'organisation</Label>
        <Input data-testid="seo-global-org-url" value={data.organization_url} onChange={(e) => set("organization_url", e.target.value)} placeholder="https://herbalife-lux.com" />
      </div>
      <div>
        <Label>Directives robots par défaut</Label>
        <Input data-testid="seo-global-robots" value={data.robots_default} onChange={(e) => set("robots_default", e.target.value)} placeholder="index,follow" />
      </div>

      <div className="pt-2">
        <Button type="submit" disabled={saving} className="bg-[#7AC143] hover:bg-[#67BC2A] text-white" data-testid="seo-global-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Enregistrer
        </Button>
      </div>
      <p className="text-xs text-[#4B634B]">Les modifications seront actives au prochain rafraîchissement de la page côté visiteur.</p>
    </form>
  );
}
